<template>
    <div class="videoWrapper in">
        <video ref="audioPlayer" class="video-js vjs-audio-only-skin"></video>
    </div>
</template>

<script>
    import videojs from 'video.js';
    import FadeMixin from '../mixins/FadeMixin';
    import SetDurationMixin from '../mixins/SetDurationMixin';

    export default {

        mixins: [
            FadeMixin,
            SetDurationMixin
        ],

        props: {
            src: {
                type: String
            },
            playlist: {
                type: Array
            },
            autoplay: {
                type: Boolean,
                default: false
            },
            playing: {
                type: Boolean,
                default: true
            },
            preload: {
                type: String,
                default: 'auto'
            },
            song_id: {
                type: Number,
                required: false,
            }
        },

        data() {
            return {
                player: null,
            };
        },

        watch: {
            src() {
                this.updatePlayer();
            },

            playlist() {
                this.updatePlayer();
            },

            playing() {
                if (!this.player) {
                    return;
                }

                console.log('Audio playing');

                if (this.playing) {
                    this.play();
                } else {
                    this.pause();
                }
            }
        },

        mounted() {
            this.createPlayer();
        },

        methods: {
            play() {
                this.player.play();
                this.fadeIn();
            },

            pause() {
                this.fadeOut().then(() => {
                    this.player.pause();
                });
            },

            createPlayer() {
                if (this.src) {
                    this.player = this.createSrcPlayer();
                } else if (this.playlist) {
                    this.player = this.createPlaylistPlayer();
                }

                this.player.on('loadeddata', () => {
                    console.log('Audio autoload (loadeddata), paused: ' + (this.player.paused() ? 'yes' : 'no') + ', autoplay: ' + (this.autoplay ? 'yes' : 'no'));

                    if (this.autoplay && this.player.paused()) {
                        console.log('Audio autoload via loadeddata');

                        this.play();
                    }
                });

                this.player.on('playlistitem', () => {
                    console.log('Audio autoload (playlistitem), paused: ' + (this.player.paused() ? 'yes' : 'no') + ', autoplay: ' + (this.autoplay ? 'yes' : 'no'));

                    if (this.autoplay && this.player.paused()) {
                        console.log('Audio autoload via playlistitem');

                        this.play();
                    }
                });

                this.player.on('play', () => {
                    this.$emit('music_play', this.song_id);
                });

                this.initFadeTimer();
            },

            updatePlayer() {
                if (!this.src) {
                    if (this.player && !this.player.paused() && this.playing) {
                        console.log('Audio not paused');
                        this.pause();
                    }

                    if (!this.playlist.length) {
                        return;
                    }
                }

                if (!this.player) {
                    this.createPlayer();
                    return;
                }

                if (this.src) {
                    this.player.src({
                        src: this.src,
                        type: 'audio/mp3'
                    });
                } else if (this.playlist) {
                    const playlist = this.getPlaylistItems();

                    this.player.playlist(playlist);
                    this.player.src(playlist[0].sources[0]);
                }

                this.player.currentTime(0);
            },

            getOptions() {
                return {
                    autoplay: false,
                    loop: false,
                    controls: true,
                    controlBar: {
                        fullscreenToggle: false,
                        pictureInPictureToggle: false,
                    },
                    preload: this.preload,
                    width: '100%',
                    height: '30px'
                };
            },

            createSrcPlayer() {
                const options = {
                    ...this.getOptions(),
                    ...{
                        sources: [
                            {
                                src: this.src,
                                type: 'audio/mp3'
                            }
                        ]
                    }
                };

                return videojs(this.$refs.audioPlayer, options);
            },

            createPlaylistPlayer() {
                const player = videojs(this.$refs.audioPlayer, this.getOptions());

                player.playlist(this.getPlaylistItems());

                player.playlist.autoadvance(0);

                return player;
            },

            getPlaylistItems() {
                return this.playlist.map(playlist => {
                    return {
                        sources: [
                            {
                                src: playlist.src,
                                type: 'audio/mp3',
                                duration: playlist.duration
                            }
                        ]
                    };
                });
            },

            getCurrentVolume() {
                if (!this.player) {
                    return 'no-player';
                }

                return Math.round(this.player.volume() * 100) + '%';
            }
        },

        beforeDestroy() {
            if (this.player) {
                console.log('Audio destroy');
                this.pause();

                setTimeout(() => {
                    this.player.dispose();
                }, this.fadeTime);
            }

            this.destroyFadeTimer();
        }
    };
</script>

<style lang="scss">
    .vjs-audio-only-skin {
        width: 100% !important;
        min-width: 200px !important;
        height: 30px !important;
        background: transparent !important;

        .vjs-big-play-button {
            display: none !important;
        }

        .vjs-control-bar {
            display: flex !important;
            border-radius: 30px !important;
            background-color: #e3e3e3 !important;
            color: #000 !important;
            opacity: 1 !important;
        }
    }

</style>
